import { CRUD, CrudModel } from '../../classes'
import { entriesToMap } from '../../utils'
import { Clinician } from '../clinician'
import { InvoiceLineItem } from '../invoice'
import { ChargeGrouping } from './charge-grouping'
import { EstimateData } from './estimate.data'
import { EstimateCharge } from './estimate-charge'
import { EstimateInputs } from './estimate-inputs'
import { EstimateInsurances } from './estimate-insurances'
import { EstimateOutput } from './estimate-output'
import { HBPricerRequest, HBPricerResponse } from './hb-pricer-api'
import { PBPricerRequest, PBPricerResponse } from './pb-pricer-api'
import { PricerRequestOptions } from './pricer-request'

@CRUD({
  base: '/estimates',
})
export class EstimateModel extends CrudModel {
  patientId: string
  facilityId: string
  templateId?: string
  appointmentIds: string[]
  status: Estimate.STATUS
  patientType?: Estimate.PATIENT_TYPE
  pricerRequestOptions?: PricerRequestOptions
  charges?: EstimateCharge[]
  pbRequests?: Map<ChargeGrouping, PBPricerRequest>
  hbRequests?: Map<ChargeGrouping, HBPricerRequest>
  pbResponses?: Map<ChargeGrouping, PBPricerResponse>
  hbResponses?: Map<ChargeGrouping, HBPricerResponse>
  inputs: EstimateInputs
  output?: EstimateOutput
  clinicianId?: string
  clinician?: Clinician
  chargeGroupings: ChargeGrouping[]
  insurances?: EstimateInsurances

  constructor(data?: EstimateData) {
    super(data, true)

    if (data?.pbRequests) {
      this.pbRequests = entriesToMap(data.pbRequests)
    }
    if (data?.hbRequests) {
      this.hbRequests = entriesToMap(data.hbRequests)
    }
    if (data?.pbResponses) {
      this.pbResponses = entriesToMap(data.pbResponses)
    }
    if (data?.hbResponses) {
      this.hbResponses = entriesToMap(data.hbResponses)
    }
    if (data?.clinician) {
      this.clinician = new Clinician(data.clinician)
    }
    this.status = data?.status ?? Estimate.STATUS.DRAFT
    this.inputs = data?.inputs ?? {
      deductible: 0,
      copayment: 0,
      coinsurance: 0,
      outOfPocketMax: 0,
      discount: 0,
      estimateType: 'patientpal',
      insuranceType: 'medical',
    }
    this.output = data?.output
    this.chargeGroupings = [
      { visitNumber: 1 },
    ]
  }

  get text(): string {
    return 'Estimate'
  }

  getChargesForGroup(chargeGrouping: ChargeGrouping): EstimateCharge[] {
    return this.charges?.filter(charge => {
      if (chargeGrouping.appointmentId != null && charge.appointmentIds?.includes(chargeGrouping.appointmentId)) {
        return true
      } else if (charge.visitNumbers?.includes(chargeGrouping.visitNumber)) {
        return true
      }
    })
  }

  getTotalPBCharges(chargeGrouping: ChargeGrouping): number {
    const charges = this.getChargesForGroup(chargeGrouping)
    // Get the total of all charges with a type of 'PB'. Remember to multiply the unit charge by the quantity
    return charges.reduce((total, charge) => {
      return charge.type === 'pb' ? total + (charge.unitCharge * charge.quantity) : total
    }, 0)
  }

  getTotalHBCharges(chargeGrouping: ChargeGrouping): number {
    const charges = this.getChargesForGroup(chargeGrouping)
    // Get the total of all charges with a type of 'HB'
    return charges.reduce((total, charge) => {
      return charge.type === 'hb' ? total + (charge.unitCharge * charge.quantity) : total
    }, 0)
  }

  get lineItems(): InvoiceLineItem[] {
    return this.charges?.map(charge => {
      const lineItem = {
        name: charge.type, // this isn't part of InvoiceLineItem, but it is apparently used in appointment estimate card
        description: charge.description ?? '',
        amount: charge.amount ?? (charge.unitCharge * charge.quantity),
        // expectedAmount: number // TODO
        currency: charge.currency,
        quantity: charge.quantity,
        unit_amount: charge.unitCharge,
      }

      return lineItem
    })
  }

  override toJSON() {
    return Object.assign(super.toJSON(), {
      patientId: this.patientId,
      facilityId: this.facilityId,
      templateId: this.templateId,
      appointmentIds: this.appointmentIds,
      status: this.status,
      patientType: this.patientType,
      charges: this.charges,
      inputs: this.inputs,
      insurances: this.insurances,
    })
  }
}

export namespace Estimate {
  export enum PATIENT_TYPE {
    InPatient = 'I',
    OutPatient = 'O',
  }

  export enum STATUS {
    DRAFT = 'D',
    CONFIRMED = 'C',
  }
}
